import { FC, useState, useEffect } from "react";
import { LocalizationViewProps } from "./localization.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Language as LanguageIcon,
  CurrencyExchange as CurrencyIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Numbers as NumbersIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  useLocalizationHook,
  useUpdateLocalizationHook,
} from "../../../hooks/useLocalizationHook";
import { useInstituteContext } from "../../../context/institute.context";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import {
  currencyList,
  formatCurrencyDisplay,
  getCurrencyByCode,
  languageList,
  formatLanguageDisplay,
  getLanguageByCode,
  dateFormatList,
  formatDateFormatDisplay,
  getDateFormatPreview,
  timeFormatList,
  formatTimeFormatDisplay,
  getTimeFormatPreview,
  numberFormatList,
  formatNumberFormatDisplay,
  getNumberFormatPreview,
} from "./localization.helpers";
import { logger } from "../../../utils/common";
import { TKeyValue } from "../../../utils/commonInterfaces";

const localizationSchema = z.object({
  currency: z
    .string()
    .min(1, "Currency selection is required")
    .refine(
      (val) => currencyList.some((c) => c.code === val),
      "Please select a valid currency",
    ),
  language: z
    .string()
    .min(1, "Language selection is required")
    .refine(
      (val) => languageList.some((l) => l.code === val),
      "Please select a valid language",
    ),
  additionalLanguages: z
    .array(z.string())
    .optional()
    .refine(
      (val) =>
        !val ||
        val.every((code) => languageList.some((l) => l.code === code)),
      "Please select valid languages",
    ),
  dateFormat: z
    .string()
    .min(1, "Date format selection is required")
    .refine(
      (val) => dateFormatList.some((d) => d.code === val),
      "Please select a valid date format",
    ),
  timeFormat: z
    .string()
    .min(1, "Time format selection is required")
    .refine(
      (val) => timeFormatList.some((t) => t.code === val),
      "Please select a valid time format",
    ),
  numberFormat: z
    .string()
    .min(1, "Number format selection is required")
    .refine(
      (val) => numberFormatList.some((n) => n.code === val),
      "Please select a valid number format",
    ),
});

type LocalizationFormData = z.infer<typeof localizationSchema>;

const LocalizationView: FC<LocalizationViewProps> = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: localization, isLoading, error } = useLocalizationHook();
  const updateLocalizationMutation = useUpdateLocalizationHook();
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LocalizationFormData>({
    resolver: zodResolver(localizationSchema),
    defaultValues: {
      currency: "",
      language: "",
      additionalLanguages: [] as string[],
      dateFormat: "",
      timeFormat: "",
      numberFormat: "",
    },
  });

  // Watch format fields for preview
  const watchedDateFormat = watch("dateFormat");
  const watchedTimeFormat = watch("timeFormat");
  const watchedNumberFormat = watch("numberFormat");

  useEffect(() => {
    if (localization && isEditMode) {
      reset({
        currency: localization.currency || "",
        language: localization.language || "",
        additionalLanguages: localization.additionalLanguages || [],
        dateFormat: localization.dateFormat || "",
        timeFormat: localization.timeFormat || "",
        numberFormat: localization.numberFormat || "",
      });
    }
  }, [localization, isEditMode, reset]);

  const handleConfigureLocalization = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (localization) {
      reset({
        currency: localization.currency || "",
        language: localization.language || "",
        additionalLanguages: localization.additionalLanguages || [],
        dateFormat: localization.dateFormat || "",
        timeFormat: localization.timeFormat || "",
        numberFormat: localization.numberFormat || "",
      });
    }
  };

  const onSubmit = async (data: LocalizationFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    try {
      // Use localization id if exists, otherwise use instituteId (API will create new entry)
      const id = localization?.id || selectedInstitute.id;
      // Exclude primary language from additional to avoid duplication
      const additionalLanguages = (data.additionalLanguages || []).filter(
        (code) => code !== data.language,
      );

      await updateLocalizationMutation.mutateAsync({
        id,
        request: {
          currency: data.currency,
          language: data.language,
          additionalLanguages:
            additionalLanguages.length > 0 ? additionalLanguages : undefined,
          dateFormat: data.dateFormat,
          timeFormat: data.timeFormat,
          numberFormat: data.numberFormat,
        },
      });
      logger.show_success("Localization settings updated successfully!");
      setIsEditMode(false);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to update localization settings. Please try again.",
      );
    }
  };

  const currencyOptions: TKeyValue<string>[] = currencyList.map((currency) => ({
    key: currency.code,
    value: `${currency.symbol} ${currency.code} - ${currency.name}`,
  }));

  const languageOptions: TKeyValue<string>[] = languageList.map((language) => ({
    key: language.code,
    value: `${language.nativeName} (${language.code.toUpperCase()}) - ${language.name}`,
  }));

  const dateFormatOptions: TKeyValue<string>[] = dateFormatList.map(
    (format) => ({
      key: format.code,
      value: `${format.name} (e.g., ${format.example})`,
    }),
  );

  const timeFormatOptions: TKeyValue<string>[] = timeFormatList.map(
    (format) => ({
      key: format.code,
      value: `${format.name} (e.g., ${format.example})`,
    }),
  );

  const numberFormatOptions: TKeyValue<string>[] = numberFormatList.map(
    (format) => ({
      key: format.code,
      value: `${format.name} (e.g., ${format.example})`,
    }),
  );

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.localization} />
        <Box p={3} data-testid="localization-view">
          <MainTitle title="Localization" />
          <Alert severity="info">
            Please select an institute from the dashboard to view localization
            settings.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.localization} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="localization-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.localization} />
        <Box p={3} data-testid="localization-view">
          <MainTitle title="Localization Settings" />
          <Alert severity="error">
            Failed to load localization settings. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  const hasLocalization =
    localization &&
    (localization.currency ||
      localization.language ||
      localization.dateFormat ||
      localization.timeFormat ||
      localization.numberFormat);

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.localization} />

      <Grid container spacing={3} data-testid="localization-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <MainTitle title="Localization Settings" />
            {!isEditMode && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleConfigureLocalization}
                size="large"
                sx={{ display: "none" }} // TODO: remove this once the API is implemented
                data-testid="configure-localization-button"
              >
                Configure Localization
              </Button>
            )}
          </Box>
        </Grid>

        {isEditMode ? (
          // Edit Mode - Localization Configuration Form
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Configure Localization
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="currency"
                        id="currency-select"
                        label="Currency"
                        itemList={currencyOptions}
                        isRequired
                        errorMessage={errors.currency?.message}
                        helperText="Select the currency for financial transactions"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="language"
                        id="language-select"
                        label="Primary Language"
                        itemList={languageOptions}
                        isRequired
                        errorMessage={errors.language?.message}
                        helperText="Select the primary language for the institute"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="additionalLanguages"
                        id="additional-languages-select"
                        label="Additional Languages"
                        itemList={languageOptions}
                        errorMessage={errors.additionalLanguages?.message}
                        helperText="Optional: Select additional languages for multi-lingual institutes"
                        multiple
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="dateFormat"
                        id="date-format-select"
                        label="Date Format"
                        itemList={dateFormatOptions}
                        isRequired
                        errorMessage={errors.dateFormat?.message}
                        helperText="Select how dates should be displayed"
                      />
                      {watchedDateFormat && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            gutterBottom
                          >
                            Preview:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {getDateFormatPreview(watchedDateFormat)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="timeFormat"
                        id="time-format-select"
                        label="Time Format"
                        itemList={timeFormatOptions}
                        isRequired
                        errorMessage={errors.timeFormat?.message}
                        helperText="Select how time should be displayed"
                      />
                      {watchedTimeFormat && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            gutterBottom
                          >
                            Preview:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {getTimeFormatPreview(watchedTimeFormat)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ControlledSelectField
                        control={control}
                        name="numberFormat"
                        id="number-format-select"
                        label="Number Format"
                        itemList={numberFormatOptions}
                        isRequired
                        errorMessage={errors.numberFormat?.message}
                        helperText="Select how numbers should be displayed"
                      />
                      {watchedNumberFormat && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            gutterBottom
                          >
                            Preview:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {getNumberFormatPreview(watchedNumberFormat)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        gap={2}
                        justifyContent="flex-end"
                        sx={{ mt: 2 }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                          disabled={
                            isSubmitting || updateLocalizationMutation.isPending
                          }
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={
                            isSubmitting || updateLocalizationMutation.isPending
                          }
                        >
                          {isSubmitting || updateLocalizationMutation.isPending
                            ? "Saving..."
                            : "Save Settings"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        ) : !hasLocalization ? (
          // Empty State
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="300px"
                  gap={2}
                >
                  <LanguageIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No Localization Configured
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    Get started by configuring currency, language, and regional
                    format preferences.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleConfigureLocalization}
                    size="large"
                    data-testid="configure-localization-empty-button"
                  >
                    Configure Localization
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Current Settings
                </Typography>
                <Grid container spacing={3}>
                  {/* Currency */}
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <CurrencyIcon color="primary" />
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Currency
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {localization?.currency
                            ? formatCurrencyDisplay(localization.currency)
                            : "Not configured"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Language */}
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <LanguageIcon color="primary" />
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Primary Language
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {localization?.language
                            ? formatLanguageDisplay(localization.language)
                            : "Not configured"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Additional Languages */}
                  {localization?.additionalLanguages &&
                    localization.additionalLanguages.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          sx={{ mb: 2 }}
                        >
                          <LanguageIcon color="primary" />
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Additional Languages
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {localization.additionalLanguages
                                .map((code) => formatLanguageDisplay(code))
                                .join(", ")}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                  {/* Date Format */}
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <CalendarIcon color="primary" />
                      <Box flex={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Date Format
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {localization?.dateFormat
                            ? formatDateFormatDisplay(localization.dateFormat)
                            : "Not configured"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Time Format */}
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <TimeIcon color="primary" />
                      <Box flex={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Time Format
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {localization?.timeFormat
                            ? formatTimeFormatDisplay(localization.timeFormat)
                            : "Not configured"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Number Format */}
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <NumbersIcon color="primary" />
                      <Box flex={1}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Number Format
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {localization?.numberFormat
                            ? formatNumberFormatDisplay(
                              localization.numberFormat,
                            )
                            : "Not configured"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default LocalizationView;
